import { defineStore } from "pinia";
import { ref } from "vue";

export type TToastStatus = "success" | "warning" | "error";

interface IToast {
  // Text of toast
  text: string;
  status: TToastStatus;
  // Id to differentiate toasts
  id: number;
}

type ToastPayload = { timeout?: number; text: string };
const defaultTimeout: number = 5000;

export const useToastStore = defineStore("toast", () => {
  const toasts = ref<IToast[]>([]);

  const timeouts = ref<Record<number, NodeJS.Timeout>>({});

  const createToast = (text: string, status: TToastStatus): IToast => ({
    text,
    status,
    id: Math.random() * 1000,
  });
  const addTimeout = (id: number, timeout: number) => {
    timeouts.value[id] = setTimeout(() => {
      let _toasts = toasts.value;
      _toasts = _toasts.filter((t) => t.id !== id);
      toasts.value = _toasts;
      delete timeouts.value[id];
    }, timeout ?? defaultTimeout);
  };

  const addToast = (payload: ToastPayload, status: TToastStatus) => {
    const { text, timeout } = payload;
    const toast = createToast(text, status);
    let _toasts = toasts.value;
    _toasts.push(toast);
    toasts.value = _toasts;
    addTimeout(toast.id, timeout ?? defaultTimeout);
  };

  function success(payload: ToastPayload | string) {
    let _payload: ToastPayload;
    if (typeof payload === "string") {
      _payload = {
        text: payload,
      };
    } else {
      _payload = payload;
    }
    addToast(_payload, "success");
  }

  function warning(payload: ToastPayload | string) {
    let _payload: ToastPayload;
    if (typeof payload === "string") {
      _payload = {
        text: payload,
      };
    } else {
      _payload = payload;
    }
    addToast(_payload, "warning");
  }

  function error(payload: ToastPayload | string) {
    let _payload: ToastPayload;
    if (typeof payload === "string") {
      _payload = {
        text: payload,
      };
    } else {
      _payload = payload;
    }
    addToast(_payload, "error");
  }

  return { toasts, success, warning, error };
});
